/**
 * Lettura di uno stream SSE.
 *
 * `EventSource` non sa fare POST e non sa mandare un header di autorizzazione,
 * quindi lo stream della chat si legge dal corpo della risposta. Trenta righe
 * invece di un WebSocket e di un secondo canale da tenere in piedi.
 *
 * Per lo stream in sola lettura resta il problema dell'autorizzazione, e la
 * risposta non è più «il token nell'URL»: si chiede un **biglietto** che vale
 * un minuto (`/api/stream/ticket`) e si mette quello. Un URL finisce nei log
 * del proxy e nella cronologia; un biglietto scaduto lì dentro non apre niente.
 */

import type { ChatEvent } from "@otto/shared";
import { api, getToken, sessioneScaduta } from "./api.ts";

export interface StreamOptions {
  signal?: AbortSignal;
  onEvent: (event: ChatEvent) => void;
}

/**
 * Quanto silenzio si sopporta prima di dire che la connessione è andata.
 *
 * Il 24/9/2026, con il server ucciso **a stream aperto**, a schermo è rimasto
 * «Cerco gli immobili…» che girava per minuti: nessun errore, il tasto ancora
 * su «Ferma», e in alto «Otto è pronto». La `fetch` non era fallita — in
 * sviluppo il browser parla con Vite, che fa da proxy, e la connessione col
 * browser è rimasta aperta e muta quando quella verso l'API è morta.
 *
 * Non è un difetto solo di sviluppo: su mobile un socket che smette di
 * ricevere **senza chiudersi** è la normalità — una galleria, un passaggio
 * wifi-dati, un bilanciatore che tiene la connessione. E il flusso della chat
 * non manda battiti, quindi il silenzio e il lavoro in corso si assomigliano.
 *
 * Quarantacinque secondi: molto più di qualunque passo vero — ogni lettura
 * emette il suo `step`, e il modello scrive di continuo — e molto meno di
 * «per sempre», che è quello che c'era prima.
 */
export const SILENZIO_MASSIMO_MS = 45_000;

export const MOTIVO_SILENZIO =
  "Il server ha smesso di rispondere a metà. Quello che hai scritto è " +
  "salvato: riprova fra un attimo.";

export const MOTIVO_TRONCATO =
  "La risposta si è interrotta a metà: la connessione è caduta prima della " +
  "fine. Quello che hai scritto è salvato.";

export async function postAndStream(
  path: string,
  body: unknown,
  options: StreamOptions,
): Promise<void> {
  const token = getToken();
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok || !response.body) {
    options.onEvent({
      type: "error",
      message:
        response.status === 401
          ? "La sessione è scaduta: entra di nuovo."
          : "Non riesco a raggiungere il server. Controlla che sia avviato.",
    });
    return;
  }

  await readSse(response.body, options.onEvent, options.signal);
}

/**
 * Stream in sola lettura (aggiornamenti delle azioni), con riconnessione.
 *
 * `onAperto` viene chiamato a ogni collegamento riuscito, e serve a chi deve
 * rimettersi in pari: **il server manda solo i cambiamenti**, e quelli
 * avvenuti mentre la connessione era giù non li rimanda a nessuno. Basta un
 * wifi che sparisce nei tre secondi dopo un'approvazione perché la card resti
 * su «la sto scrivendo…» per sempre, mentre sul gestionale è già fatta.
 */
export function openEventStream(
  path: string,
  onEvent: (event: ChatEvent) => void,
  onAperto?: () => void,
): () => void {
  const controller = new AbortController();
  let stopped = false;
  let attempt = 0;

  const connect = async () => {
    while (!stopped) {
      try {
        // Senza sessione non c'è niente da riconnettere: insistere vorrebbe
        // dire bussare ogni trenta secondi con un token morto, per sempre.
        if (sessioneScaduta() || !getToken()) return;

        const { ticket } = await api.streamTicket();
        const response = await fetch(
          `${path}?ticket=${encodeURIComponent(ticket)}`,
          { signal: controller.signal },
        );
        if (!response.ok || !response.body) {
          // 401 qui vuol dire sessione finita: `api` l'ha già registrato, e
          // il giro dopo questo ciclo si ferma da solo.
          throw new Error(String(response.status));
        }

        attempt = 0;
        onAperto?.();
        // Il server manda un `ping` a intervalli regolari: un silenzio molto
        // più lungo di quello vuol dire connessione morta, e il modo di
        // ripararla è uscire di qui — il ciclo si riconnette da solo.
        await readSse(response.body, onEvent, controller.signal, {
          attesaMs: SILENZIO_MASSIMO_MS,
          segnala: false,
        });
      } catch {
        if (stopped) return;
        if (sessioneScaduta()) return;
      }
      // Riconnessione con attesa crescente, al massimo mezzo minuto.
      attempt++;
      const wait = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  };

  void connect();

  return () => {
    stopped = true;
    controller.abort();
  };
}

/** Sentinella: il `read()` non è arrivato in tempo. */
const SILENZIO = Symbol("silenzio");

/**
 * `reader.read()`, ma con una sveglia.
 *
 * `Promise.race` da sola lascerebbe il timer acceso quando la lettura vince,
 * e su uno stream lungo sarebbero decine di timer appesi: si spegne sempre.
 */
async function leggiOppureArrenditi(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  attesaMs: number,
): Promise<ReadableStreamReadResult<Uint8Array> | typeof SILENZIO> {
  let sveglia: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      reader.read(),
      new Promise<typeof SILENZIO>((resolve) => {
        sveglia = setTimeout(() => resolve(SILENZIO), attesaMs);
      }),
    ]);
  } finally {
    if (sveglia !== undefined) clearTimeout(sveglia);
  }
}

/**
 * `segnala: false` per lo stream degli aggiornamenti.
 *
 * Lì il silenzio si ripara da solo — il ciclo si riconnette e `onAperto`
 * rimette in pari chi deve — e una chiusura pulita è normale, perché quello
 * stream non finisce mai con un `message_done`. Dire «la risposta si è
 * interrotta» a ogni riconnessione sarebbe rumore su una cosa che funziona.
 */
async function readSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: ChatEvent) => void,
  signal?: AbortSignal,
  opzioni: { attesaMs?: number; segnala?: boolean } = {},
): Promise<void> {
  const attesaMs = opzioni.attesaMs ?? SILENZIO_MASSIMO_MS;
  const segnala = opzioni.segnala ?? true;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // Un turno finisce con `message_done` (o con un `error` dal server). Se lo
  // stream si chiude senza, la risposta è stata tagliata a metà e va detto.
  let finito = false;

  try {
    while (!signal?.aborted) {
      const letto = await leggiOppureArrenditi(reader, attesaMs);

      if (letto === SILENZIO) {
        if (segnala) onEvent({ type: "error", message: MOTIVO_SILENZIO });
        return;
      }

      const { done, value } = letto;
      if (done) {
        // Chiusa dopo aver detto tutto: è la fine normale, non un guasto.
        if (segnala && !finito && !signal?.aborted) {
          onEvent({ type: "error", message: MOTIVO_TRONCATO });
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Gli eventi SSE sono separati da una riga vuota.
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const evento = JSON.parse(payload) as ChatEvent;
            if (evento.type === "message_done" || evento.type === "error") {
              finito = true;
            }
            onEvent(evento);
          } catch {
            /* Un evento malformato non deve interrompere lo stream. */
          }
        }

        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    // Arrendersi per silenzio lascia il corpo ancora aperto: va chiuso, o la
    // connessione resta appesa finché non se ne accorge il browser.
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
